import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { activePb, openProtectedFile } from '../../lib/pb';
import { useData } from '../../data/DataProvider';
import { money, date, invoiceTotal } from '../../shared/formatters';
import StatusPill from '../../shared/components/StatusPill';
import EmptyState from '../../shared/components/EmptyState';
import SlideOver from '../../shared/components/SlideOver';
import FileDropzone from '../../shared/components/FileDropzone';
import JobPhotoStrip from '../../shared/components/JobPhotoStrip';
import ConfirmDialog from '../../shared/components/ConfirmDialog';
import CertificateForm from '../certificates/CertificateForm';
import JobForm from '../jobs/JobForm';
import ClientForm from './ClientForm';
import PropertyForm from './PropertyForm';
import PaymentForm from './PaymentForm';
import { QuoteStatusPill } from '../quotes/QuotesList';
import { portalApi } from '../../lib/api';
import { useToast } from '../../shared/components/ToastProvider';
import SafeIcon from '../../common/SafeIcon';
import { Icons } from '../../shared/icons';
import ActivityTab from './ActivityTab';

const tabs = ['Overview', 'Properties', 'Activity', 'Work History', 'Invoices', 'Payments', 'Key Dates', 'Notes', 'Messages', 'Documents', 'Quotes', 'Certificates'];
const DOC_TYPES = ['invoice', 'estimate', 'quote'];
const DOC_TYPE_CHIPS = { invoice: 'Invoices', estimate: 'Estimates', quote: 'Quotes' };
const DAY = 86400000;

// Quick actions (spec §2 / engineering brief §7.1) — each maps to a URL
// panel (drawer) or a route. The old "+ Add record" button is preserved as
// "New job" (panel=job) at the top of the menu.
const QUICK_ACTIONS = [
  { panel: 'job', label: 'New job', icon: Icons.Tool },
  { panel: 'invoice', label: 'New invoice', icon: Icons.FileText, route: true },
  { panel: 'payment', label: 'Record payment', icon: Icons.Cash },
  { panel: 'keydate', label: 'Key date', icon: Icons.Calendar },
  { panel: 'note', label: 'Note', icon: Icons.Pen },
  { panel: 'upload', label: 'Upload document', icon: Icons.Upload },
  { panel: 'quote', label: 'New quote', icon: Icons.FileText, route: true },
  { panel: 'certificate', label: 'Add certificate', icon: Icons.Shield },
];

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, add, update, remove, ready } = useData();
  const { notify } = useToast();
  const [client, setClient] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [busy, setBusy] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadType, setUploadType] = useState('other');
  const [uploadRequiresAck, setUploadRequiresAck] = useState(false);
  const [docFilter, setDocFilter] = useState('invoice');
  const [confirm, setConfirm] = useState(null); // { kind, payment | property | keyDate | doc | invoice }
  const [menuOpen, setMenuOpen] = useState(false);
  const [showArchivedDocs, setShowArchivedDocs] = useState(false);
  const [showArchivedPayments, setShowArchivedPayments] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = no search, [] = no results, array = results
  const menuRef = useRef(null);

  // URL-addressable drawers (spec §4): panel + recordId search params are
  // the single source of truth for what's open — refresh/back reopens the
  // same drawer with the same record.
  const panel = searchParams.get('panel');
  const recordId = searchParams.get('recordId');

  const openPanel = (name, rid) => {
    setMenuOpen(false);
    setSearchParams(rid ? { panel: name, recordId: rid } : { panel: name });
  };
  const closePanel = () => setSearchParams({});

  const editingNote = panel === 'note' ? (recordId ? data.notes.find((n) => n.id === recordId) : null) : null;
  const editingKeyDate = panel === 'keydate' ? (recordId ? data.keyDates.find((k) => k.id === recordId) : null) : null;
  const editJob = panel === 'edit-job' ? data.jobs.find((j) => j.id === recordId) : null;
  const editProperty = panel === 'property'
    ? (recordId ? data.properties.find((p) => p.id === recordId) : { client_id: client?.id || id })
    : null;
  const editClient = panel === 'edit-client' ? client : null;

  // Close the quick-action menu on outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  // Find the client once data is ready
  useEffect(() => {
    if (ready && data.clients.length > 0) {
      const found = data.clients.find((item) => item.id === id) || data.clients[0];
      setClient(found);
    }
  }, [ready, data.clients, id]);

  // Show loading state while client data is being fetched
  if (!ready || !client) {
    return <div className="admin-page"><div className="loading">Loading client details…</div></div>;
  }

  const clientJobs = data.jobs.filter((item) => item.client_id === client.id);
  const clientProperties = data.properties.filter((item) => item.client_id === client.id);
  const clientInvoices = data.invoices.filter((item) => item.client_id === client.id);
  const filteredInvoices = clientInvoices.filter((item) => (item.docType || 'invoice') === docFilter);
  const clientPayments = data.payments.filter((item) => item.client_id === client.id);
  const clientDates = data.keyDates.filter((item) => item.client_id === client.id);
  const clientMessages = data.messages.filter((item) => item.client_id === client.id);
  const clientDocuments = data.documents.filter((item) => item.client_id === client.id);
  const clientQuotes = data.quotes.filter((item) => item.client_id === client.id);
  const clientCertificates = data.certificates.filter((item) => item.client_id === client.id);
  const clientNotes = data.notes
    .filter((item) => item.client_id === client.id)
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || new Date(b.created_at) - new Date(a.created_at));
  const totalInvoiced = clientInvoices
    .filter((inv) => (inv.docType || 'invoice') === 'invoice')
    .reduce((sum, invoice) => sum + invoiceTotal(invoice), 0);
  const totalPaid = clientPayments
    .filter((p) => p.status !== 'rejected' && !p.archived)
    .reduce((sum, payment) => sum + payment.amount, 0);
  const pendingPayments = clientPayments.filter((p) => p.status === 'pending');

  async function uploadDocument(event) {
    event.preventDefault();
    if (!uploadFiles.length) {
      notify('Choose a file first', 'error');
      return;
    }
    setBusy(true);
    try {
      for (const file of uploadFiles) {
        await add('documents', { client_id: client.id, type: uploadType, requires_acknowledgement: uploadRequiresAck, file });
      }
      setUploadFiles([]);
      closePanel();
      notify(`Uploaded ${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''}`);
    } catch (err) {
      notify(err.message || 'Upload failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  function saveKeyDate(event) {
    event.preventDefault();
    setBusy(true);
    (async () => {
      try {
        const payload = {
          client_id: client.id,
          type: event.target.type.value,
          date: event.target.date.value,
          detail: event.target.detail.value,
          recurring: event.target.recurring?.checked || false,
        };
        if (editingKeyDate) {
          await update('keyDates', editingKeyDate.id, { ...editingKeyDate, ...payload });
          notify('Key date updated');
        } else {
          await add('keyDates', payload);
          notify('Key date added');
        }
        closePanel();
      } catch (err) {
        notify(err.message || 'Failed to save key date', 'error');
      } finally {
        setBusy(false);
      }
    })();
  }

  async function deleteKeyDate(keyDate) {
    setBusy(true);
    try {
      await remove('keyDates', keyDate.id);
      notify('Key date deleted');
    } catch (err) {
      notify(err.message || 'Failed to delete key date', 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  async function saveNote(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const payload = {
        client_id: client.id,
        author: editingNote?.author || activePb().authStore.record?.name || 'Staff',
        body: event.target.body.value,
        pinned: event.target.pinned.checked,
        client_visible: event.target.client_visible.checked,
      };
      if (editingNote) {
        await update('notes', editingNote.id, { ...editingNote, ...payload });
        notify('Note updated');
      } else {
        await add('notes', payload);
        notify('Note added');
      }
      closePanel();
    } catch (err) {
      notify(err.message || 'Failed to save note', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function settlePayment(payment, status) {
    setBusy(true);
    try {
      await update('payments', payment.id, { ...payment, status });
      if (status === 'confirmed') {
        const invoice = data.invoices.find((inv) => inv.id === payment.invoice_id);
        if (invoice && invoice.status !== 'paid') {
          await update('invoices', invoice.id, {
            ...invoice,
            status: 'paid',
            paid_date: new Date().toISOString().slice(0, 10),
          });
        }
        notify('Payment confirmed — invoice marked paid');
      } else {
        notify('Payment claim rejected');
      }
    } catch (err) {
      notify(err.message || 'Failed to update payment', 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  // Reversal (spec §2.2/§3): confirmed payments can't be deleted — archive
  // instead; the pb_hook recomputes the invoice's paid state.
  async function reversePayment(payment) {
    setBusy(true);
    try {
      await update('payments', payment.id, { ...payment, archived: true });
      notify('Payment reversed');
    } catch (err) {
      notify(err.message || 'Failed to reverse payment', 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  async function deleteNote(noteId) {
    try {
      await remove('notes', noteId);
      notify('Note deleted');
    } catch (err) {
      notify(err.message || 'Failed to delete note', 'error');
    }
  }

  // Remove a property (jobs keep their site_* snapshot; the relation clears).
  async function deleteProperty(propertyId) {
    setBusy(true);
    try {
      await remove('properties', propertyId);
      notify('Property removed');
    } catch (err) {
      notify(err.message || 'Failed to remove property', 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  // Soft-delete a document (spec §3): archive keeps it recoverable; hard
  // delete is only offered for unacknowledged v1 files (server guard backs
  // this up in documents.pb.js).
  async function archiveDocument(document, archived) {
    setBusy(true);
    try {
      await update('documents', document.id, { ...document, archived });
      notify(archived ? 'Document archived' : 'Document restored');
    } catch (err) {
      notify(err.message || 'Failed to update document', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function replaceDocument(document, file) {
    setBusy(true);
    try {
      await update('documents', document.id, { ...document, file });
      notify('Document replaced — version bumped');
    } catch (err) {
      notify(err.message || 'Failed to replace document', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function deleteDocument(document) {
    setBusy(true);
    try {
      await remove('documents', document.id);
      notify('Document deleted');
    } catch (err) {
      notify(err.message || err.data?.message || 'Failed to delete document — archive it instead', 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  async function voidInvoice(invoice) {
    setBusy(true);
    try {
      await update('invoices', invoice.id, { ...invoice, status: 'void' });
      notify(`Invoice ${invoice.number || ''} voided`);
    } catch (err) {
      notify(err.message || 'Failed to void invoice', 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  // Email the client a fresh one-click OTP sign-in link (spec §6 — the
  // "Resend access" action replacing the old copy-link flow). Recovery
  // path when a client's session expired or they lost their link.
  async function resendPortalAccess(targetClient) {
    setBusy(true);
    try {
      const result = await portalApi.sendPortalAccess(targetClient.id);
      notify(`Sign-in link sent to ${result.email || targetClient.email}`);
    } catch (err) {
      notify(err.message || 'Failed to send access email', 'error');
    } finally {
      setBusy(false);
    }
  }

  // Archive/delete client functionality
  async function archiveClient() {
    setBusy(true);
    try {
      await update('clients', client.id, { ...client, status: 'archived' });
      notify('Client archived successfully');
      navigate('/admin/clients');
    } catch (err) {
      notify(err.message || 'Failed to archive client', 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  async function deleteClient() {
    setBusy(true);
    try {
      await remove('clients', client.id);
      notify('Client deleted successfully');
      navigate('/admin/clients');
    } catch (err) {
      notify(err.message || 'Failed to delete client', 'error');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  // Search functionality
  function performSearch() {
    if (!searchTerm.trim()) {
      setSearchResults(null);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const results = {
      clients: [],
      invoices: [],
      jobs: []
    };

    // Search clients
    if (data.clients) {
      results.clients = data.clients.filter((c) =>
        c.name.toLowerCase().includes(term) ||
        c.email.toLowerCase().includes(term) ||
        c.phone.toLowerCase().includes(term) ||
        c.address.toLowerCase().includes(term)
      );
    }

    // Search invoices
    if (data.invoices) {
      results.invoices = data.invoices.filter((invoice) =>
        invoice.number.toLowerCase().includes(term) ||
        invoice.title.toLowerCase().includes(term) ||
        (invoice.docType && invoice.docType.toLowerCase().includes(term))
      );
    }

    // Search jobs
    if (data.jobs) {
      results.jobs = data.jobs.filter((job) =>
        job.title.toLowerCase().includes(term) ||
        job.description.toLowerCase().includes(term) ||
        job.status.toLowerCase().includes(term)
      );
    }

    setSearchResults(results);
  }

  const quickAction = (action) => {
    if (action.panel === 'invoice') {
      navigate(`/admin/invoices/new?client=${client.id}`);
      return;
    }
    if (action.panel === 'quote') {
      navigate(`/admin/quotes/new?client=${client.id}`);
      return;
    }
    openPanel(action.panel);
  };

  return <div className="admin-page"><header className="admin-topbar"><div><SafeIcon icon={Icons.Search} /><input 
  placeholder="Search clients, invoices or jobs…" 
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  onKeyPress={(e) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  }}
/></div></header>
  
  {/* Search results overlay */}
  {searchResults && (
    <div className="search-results-overlay">
      <div className="search-results-header">
        <h3>Search Results</h3>
        <button className="secondary-button" onClick={() => setSearchResults(null)}>
          <SafeIcon icon={Icons.X} size={14} /> Close
        </button>
      </div>
      <div className="search-results-content">
        {searchResults.clients.length > 0 && (
          <div className="search-section">
            <h4>Clients ({searchResults.clients.length})</h4>
            <div className="search-list">
              {searchResults.clients.map((c) => (
                <div key={c.id} className="search-item">
                  <div className="search-item-name">{c.name}</div>
                  <div className="search-item-info">
                    <span>{c.email}</span>
                    <span>{c.phone}</span>
                  </div>
                  <button className="primary-button" onClick={() => {
                    navigate(`/admin/clients/${c.id}`);
                    setSearchResults(null);
                  }}>
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {searchResults.invoices.length > 0 && (
          <div className="search-section">
            <h4>Invoices ({searchResults.invoices.length})</h4>
            <div className="search-list">
              {searchResults.invoices.map((invoice) => (
                <div key={invoice.id} className="search-item">
                  <div className="search-item-name">Invoice #{invoice.number}</div>
                  <div className="search-item-info">
                    <span>{invoice.title}</span>
                    <span>{invoice.docType || 'invoice'}</span>
                  </div>
                  <button className="primary-button" onClick={() => {
                    navigate(`/admin/invoices/${invoice.id}/edit`);
                    setSearchResults(null);
                  }}>
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {searchResults.jobs.length > 0 && (
          <div className="search-section">
            <h4>Jobs ({searchResults.jobs.length})</h4>
            <div className="search-list">
              {searchResults.jobs.map((job) => (
                <div key={job.id} className="search-item">
                  <div className="search-item-name">{job.title}</div>
                  <div className="search-item-info">
                    <span>{job.status}</span>
                    <span>{job.description}</span>
                  </div>
                  <button className="primary-button" onClick={() => {
                    navigate(`/admin/jobs/${job.id}`);
                    setSearchResults(null);
                  }}>
                    View
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {searchResults.clients.length === 0 && searchResults.invoices.length === 0 && searchResults.jobs.length === 0 && (
          <div className="search-section">
            <EmptyState title="No results found" />
          </div>
        )}
      </div>
    </div>
  )}
  
  <div className="client-hero"><Link className="back-link" to="/admin/clients"><SafeIcon icon={Icons.ArrowLeft} /> Clients</Link><div className="client-heading"><div className="client-avatar">{client.initials}</div><div><div className="heading-line"><h1>{client.name}</h1><span className="active-client">{client.status}</span></div><p>{client.address}</p></div><div className="hero-actions"><button className="icon-button secondary-button" onClick={() => openPanel('edit-client')} title="Edit client"><SafeIcon icon={Icons.Edit} /></button><div className="quick-action-wrap" ref={menuRef}><button className="primary-button" onClick={() => setMenuOpen((open) => !open)}>+ Add record <SafeIcon icon={Icons.ChevronDown} size={14} /></button>{menuOpen && <div className="quick-action-menu">{QUICK_ACTIONS.map((action) => <button key={action.panel} onClick={() => quickAction(action)}><SafeIcon icon={action.icon} /> {action.label}</button>)}</div>}</div><button className="secondary-button" onClick={() => resendPortalAccess(client)} disabled={busy || !client.email}>{busy ? 'Sending…' : 'Resend access'}</button><button className="danger-button" onClick={() => setConfirm({ kind: 'delete', action: 'archive' })} title="Archive client"><SafeIcon icon={Icons.Archive} /></button></div></div><div className="client-tabs">{tabs.map((item) => <button className={tab === item ? 'active' : ''} onClick={() => setTab(item)} key={item}>{item}{item === 'Payments' && pendingPayments.length ? ` (${pendingPayments.length})` : ''}</button>)}</div></div><main className="content-wrap">
    {tab === 'Properties' && <section className="panel tab-panel"><div className="panel-head"><h3>Properties</h3><button className="primary-button" onClick={() => openPanel('property')}>+ Add property</button></div>{clientProperties.length ? <div className="note-list">{clientProperties.map((property) => <article className="note-card" key={property.id}><div className="note-head"><strong>{property.name || 'Unnamed property'}</strong><span>{property.status}{property.address ? ` · ${property.address}` : ''}</span><span className="note-actions"><button className="link-button" onClick={() => openPanel('property', property.id)}>Edit</button><button className="link-button danger-link" onClick={() => setConfirm({ kind: 'delete-property', property })}>Remove</button></span></div>{property.notes ? <p>{property.notes}</p> : null}</article>)}</div> : <EmptyState title="No properties yet" message="A client can have multiple properties — e.g. a home, a holiday let or an office. Jobs can be linked to a property to prefill the site address." action="Add property" onAction={() => openPanel('property')} />}</section>}
    {tab === 'Activity' && <ActivityTab client={client} />}
    {tab === 'Overview' && <div className="overview-grid"><section className="panel span-two"><div className="metric-row"><div><span>Outstanding</span><strong className="balance">{money(totalInvoiced - totalPaid)}</strong><small>{clientInvoices.length} documents</small></div><div><span>Total jobs</span><strong>{clientJobs.length}</strong><small>Work records</small></div><div><span>Total paid</span><strong>{money(totalPaid)}</strong><small>Recorded payments</small></div></div></section><section className="panel"><div className="panel-head"><h3>Contact details</h3></div><dl className="detail-list"><dt>Email</dt><dd>{client.email}</dd><dt>Phone</dt><dd>{client.phone}</dd><dt>Address</dt><dd>{client.address}</dd><dt>Portal</dt><dd><button className="secondary-button" onClick={() => resendPortalAccess(client)} disabled={busy || !client.email}>{busy ? 'Sending…' : 'Resend access'}</button><small className="field-hint">Emails the client a one-click sign-in link, valid 10 minutes.</small></dd></dl></section><section className="panel"><div className="panel-head"><h3>Next key date</h3></div><div className="keydate-list compact">{clientDates.slice(0, 1).map((kd) => <div className="keydate-row" key={kd.id}><div className={`date-icon date-${kd.type}`}><SafeIcon icon={Icons.Calendar} /></div><div className="keydate-copy"><strong>{kd.title}</strong><p>{date(kd.date)}</p></div></div>)}</div></section></div>}
    {tab === 'Work History' && <section className="panel tab-panel"><div className="panel-head"><h3>Work history</h3><button className="primary-button" onClick={() => openPanel('job')}>+ New job</button></div>{clientJobs.length ? <div className="job-list">{clientJobs.map((job) => <article key={job.id}><i><SafeIcon icon={Icons.Tool} /></i><div><strong>{job.title}</strong><span>{date(job.date)} · {job.engineer}</span><p>{job.description}</p><StatusPill status={job.status} /><JobPhotoStrip job={job} /><button type="button" className="link-button" onClick={() => openPanel('edit-job', job.id)}>Edit job</button></div></article>)}</div> : <EmptyState title="No jobs yet" action="Add job" onAction={() => openPanel('job')} />}</section>}
    {tab === 'Invoices' && <section className="panel tab-panel"><div className="panel-head"><h3>Billing documents</h3><div className="header-actions"><div className="filter-chips">{DOC_TYPES.map((type) => <button key={type} className={`chip ${docFilter === type ? 'chip-active' : ''}`} onClick={() => setDocFilter(type)}>{DOC_TYPE_CHIPS[type]}</button>)}</div><button className="primary-button" onClick={() => navigate(`/admin/invoices/new?client=${client.id}`)}>+ New {DOC_TYPE_CHIPS[docFilter].toLowerCase()}</button></div></div>{filteredInvoices.length ? filteredInvoices.map((invoice) => <div className="invoice-row" key={invoice.id}><Link className="invoice-row-main" to={`/admin/invoices/${invoice.id}/edit`}><div><strong>{invoice.number}</strong><span>{invoice.title}</span></div><span>Due {date(invoice.due_date)}</span><strong>{money(invoiceTotal(invoice))}</strong></Link><StatusPill invoice={invoice} />{invoice.status !== 'void' && <button className="link-button danger-link" onClick={() => setConfirm({ kind: 'void-invoice', invoice })}>Void</button>}<button className="link-button" onClick={() => navigate(`/admin/invoices/new?duplicate=${invoice.id}&client=${invoice.client_id}`)} title="Duplicate">Duplicate</button>{invoice.pdf ? <button className="link-button" onClick={() => openProtectedFile({ id: invoice.id, collectionName: 'invoices' }, invoice.pdf)}>PDF</button> : null}</div>) : <EmptyState title={`No ${DOC_TYPE_CHIPS[docFilter].toLowerCase()} yet`} action={`New ${DOC_TYPE_CHIPS[docFilter].toLowerCase()}`} onAction={() => navigate(`/admin/invoices/new?client=${client.id}`)} />}</section>}
    {tab === 'Payments' && <section className="panel tab-panel"><div className="panel-head"><h3>Payments{pendingPayments.length ? <span className="nav-badge">{pendingPayments.length} pending</span> : ''}</h3><div className="header-actions"><label className="checkbox-row"><input type="checkbox" checked={showArchivedPayments} onChange={(e) => setShowArchivedPayments(e.target.checked)} /> Show reversed</label><button className="primary-button" onClick={() => openPanel('payment')}>+ Record payment</button></div></div>{clientPayments.filter((p) => showArchivedPayments || !p.archived).length ? clientPayments.filter((p) => showArchivedPayments || !p.archived).map((payment) => <div className={`invoice-row ${payment.archived ? 'row-muted' : ''}`} key={payment.id}><div><strong>{money(payment.amount)}</strong><span>{payment.reference}</span></div><span>{date(payment.date)}</span><span>{payment.method}</span><span>{data.invoices.find((invoice) => invoice.id === payment.invoice_id)?.number}</span>{payment.archived ? <span className="status-pill">Reversed</span> : payment.status === 'pending' ? <span className="status-pill pending">Pending review {busy ? '…' : <><button className="secondary-button" onClick={() => setConfirm({ kind: 'confirm', payment })}>Confirm</button><button className="secondary-button" onClick={() => setConfirm({ kind: 'reject', payment })}>Reject</button></>}</span> : <StatusPill status={payment.status === 'rejected' ? 'cancelled' : 'paid'} />}{!payment.archived && payment.status === 'confirmed' && <button className="link-button danger-link" onClick={() => setConfirm({ kind: 'reverse', payment })}>Reverse</button>}</div>) : <EmptyState title="No payments yet" action="Record payment" onAction={() => openPanel('payment')} />}</section>}
    {tab === 'Key Dates' && <section className="panel tab-panel"><div className="panel-head"><h3>Key dates</h3><button className="primary-button" onClick={() => openPanel('keydate')}>+ Add key date</button></div>{clientDates.length ? <div className="keydate-list">{clientDates.map((kd) => { const synced = kd.detail?.startsWith('Certificate '); return <div className="keydate-row" key={kd.id}><div className={`date-icon date-${kd.type}`}><SafeIcon icon={Icons.Calendar} /></div><div className="keydate-copy"><div className="keydate-heading"><strong>{kd.title}</strong><span className={`date-tag date-${kd.type}`}>{kd.recurring ? 'Recurring' : 'One-off'}</span></div><p>{date(kd.date)}{kd.detail ? ` · ${kd.detail}` : ''}</p></div><span className="note-actions"><button className="link-button" onClick={() => openPanel('keydate', kd.id)}>Edit</button>{synced ? <span className="field-hint" title="This date is synced from a certificate — edit the certificate instead">synced</span> : <button className="link-button danger-link" onClick={() => setConfirm({ kind: 'delete-keydate', keyDate: kd })}>Delete</button>}</span></div>; })}</div> : <EmptyState title="No key dates yet" action="Add key date" onAction={() => openPanel('keydate')} />}</section>}
    {tab === 'Notes' && <section className="panel tab-panel"><div className="panel-head"><h3>Site notes</h3><button className="primary-button" onClick={() => openPanel('note')}>+ Add note</button></div>{clientNotes.length ? <div className="note-list">{clientNotes.map((note) => <article className={`note-card ${note.pinned ? 'pinned' : ''}`} key={note.id}><div className="note-head"><strong>{note.author || 'Staff'} · {date(note.created_at)}</strong><span>{note.client_visible ? 'Visible to client' : 'Internal'}{note.pinned ? ' · Pinned' : ''}</span><span className="note-actions"><button className="link-button" onClick={() => openPanel('note', note.id)}>Edit</button><button className="icon-button" aria-label="Delete note" onClick={() => deleteNote(note.id)}><SafeIcon icon={Icons.X} /></button></span></div><p>{note.body}</p></article>)}</div> : <EmptyState title="No notes yet" action="Add note" onAction={() => openPanel('note')} />}</section>}
    {tab === 'Messages' && <section className="panel tab-panel"><h3>Messages</h3><div className="chat-list">{clientMessages.map((message) => <div className={`message ${message.sender}`} key={message.id}><small>{message.name} · {date(message.created_at)}</small><p>{message.body}</p></div>)}</div></section>}
    {tab === 'Documents' && <section className="panel tab-panel"><div className="panel-head"><h3>Documents</h3><div className="header-actions"><label className="checkbox-row"><input type="checkbox" checked={showArchivedDocs} onChange={(e) => setShowArchivedDocs(e.target.checked)} /> Show archived</label><button className="primary-button" onClick={() => openPanel('upload')}>+ Upload document</button></div></div>{clientDocuments.filter((d) => showArchivedDocs || !d.archived).length ? <div className="document-grid">{clientDocuments.filter((d) => showArchivedDocs || !d.archived).map((document) => { const ackStamp = `v${document.version || 1}:${document.filename}`; const docAck = (data.documentAcks || []).find((a) => a.document_id === document.id && a.document_version === ackStamp); return <article className={`document-card ${document.archived ? 'row-muted' : ''}`} key={document.id}><SafeIcon icon={Icons.FileText} /><strong>{document.filename}</strong><small>{date(document.uploaded_at)}{document.version > 1 ? ` · v${document.version}` : ''}{document.archived ? ' · archived' : ''}</small>{docAck ? <span className="status-pill status-completed"><span className="status-dot" />Acknowledged ✓ {date(docAck.acknowledged_at)}</span> : document.requiresAcknowledgement ? <span className="status-pill status-pending"><span className="status-dot" />Pending acknowledgement</span> : null}<div className="note-actions">{document.filename && document.filename !== 'document' ? <button className="link-button" onClick={() => openProtectedFile(document, document.filename)}>View</button> : null}{!document.archived ? <label className="link-button" style={{ cursor: 'pointer' }}>Replace<input type="file" hidden accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => { if (e.target.files?.[0]) replaceDocument(document, e.target.files[0]); e.target.value = ''; }} /></label> : null}<button className="link-button" onClick={() => archiveDocument(document, !document.archived)}>{document.archived ? 'Restore' : 'Archive'}</button>{!document.archived && !document.requiresAcknowledgement && document.version <= 1 && !docAck ? <button className="link-button danger-link" onClick={() => setConfirm({ kind: 'delete-doc', document })}>Delete</button> : null}</div></article>; })}</div> : <EmptyState title="No documents yet" action="Upload document" onAction={() => openPanel('upload')} />}</section>}
    {tab === 'Quotes' && <section className="panel tab-panel"><div className="panel-head"><h3>Quotes</h3><button className="primary-button" onClick={() => navigate(`/admin/quotes/new?client=${client.id}`)}>+ New quote</button></div>{clientQuotes.length ? <div className="invoice-list">{clientQuotes.map((quote) => <button type="button" className="invoice-row" style={{ width: '100%', textAlign: 'left', cursor: 'pointer' }} key={quote.id} onClick={() => navigate(`/admin/quotes/${quote.id}`)}><div><strong>{quote.number}</strong><span>{quote.title}</span></div><span>{quote.valid_until ? `Valid until ${date(quote.valid_until)}` : '—'}</span><strong>{money(invoiceTotal(quote))}</strong><QuoteStatusPill status={quote.status} /></button>)}</div> : <EmptyState title="No quotes yet" action="New quote" onAction={() => navigate(`/admin/quotes/new?client=${client.id}`)} />}</section>}
    {tab === 'Certificates' && <section className="panel tab-panel"><div className="panel-head"><h3>Certificates</h3><button className="primary-button" onClick={() => openPanel('certificate')}>+ Add certificate</button></div>{clientCertificates.length ? <div className="invoice-list">{clientCertificates.map((cert) => { const days = cert.expiry_date ? Math.round((new Date(cert.expiry_date) - Date.now()) / DAY) : null; const pill = days == null ? 'draft' : days < 0 ? 'overdue' : days <= 30 ? 'pending' : 'paid'; const cp12 = cert.type === 'gas_safety_cp12' && cert.details && Object.keys(cert.details).length ? cert.details : null; return <div className="invoice-row" key={cert.id}><div><strong>{cert.certificate_number || cert.typeLabel}</strong><span>{cert.typeLabel}{cert.notes ? ` · ${cert.notes}` : ''}{cp12 && (cp12.property_address || cp12.appliances?.length) ? <small className="cp12-line">{cp12.property_address || ''}{cp12.appliances?.length ? `${cp12.property_address ? ' — ' : ''}${cp12.appliances.length} appliance${cp12.appliances.length > 1 ? 's' : ''}` : ''}</small> : null}</span></div><span>Issued {date(cert.issued_date)}</span><span>Expires {date(cert.expiry_date)}</span><span className={`status-pill status-${pill}`}><span className="status-dot" />{days == null ? 'No expiry' : days < 0 ? `Expired ${Math.abs(days)}d ago` : days <= 30 ? `Expires in ${days}d` : `Valid · ${days}d`}</span>{cert.filename ? <button className="link-button" onClick={() => openProtectedFile(cert, cert.filename)}>View</button> : null}</div>; })}</div> : <EmptyState title="No certificates yet" action="Add certificate" onAction={() => openPanel('certificate')} />}</section>}
    </main>

  <JobForm open={panel === 'job' || panel === 'edit-job'} job={editJob} clientId={client.id} onClose={closePanel} />

  <PaymentForm open={panel === 'payment'} clientId={client.id} onClose={closePanel} />

  <SlideOver open={panel === 'upload'} title="Upload document" onClose={closePanel}><form className="drawer-form" onSubmit={uploadDocument}><label>Type<select value={uploadType} onChange={(e) => setUploadType(e.target.value)}><option value="gas_safety_cert">Gas safety certificate</option><option value="quote">Quote</option><option value="photo">Photo</option><option value="other">Other</option></select></label><FileDropzone onFiles={setUploadFiles} accept="application/pdf,image/jpeg,image/png,image/webp" hint="PDF or image, up to 10 MB" onReject={(msgs) => notify(msgs.join('. '), 'error')} />{uploadFiles.length ? <ul className="file-list">{uploadFiles.map((file) => <li key={file.name}>{file.name}</li>)}</ul> : null}<label className="checkbox-row"><input type="checkbox" checked={uploadRequiresAck} onChange={(e) => setUploadRequiresAck(e.target.checked)} /> Require client acknowledgement</label><button className="primary-button" disabled={busy}>{busy ? 'Uploading…' : 'Upload'}</button></form></SlideOver>

  <SlideOver open={panel === 'keydate'} title={editingKeyDate ? 'Edit key date' : 'Add key date'} onClose={closePanel}><form className="drawer-form" key={editingKeyDate?.id || 'keydate-new'} onSubmit={saveKeyDate}><label>Type<select name="type" defaultValue={editingKeyDate?.type || 'service'}><option value="service">Boiler service</option><option value="cert">Gas safety certificate</option><option value="job">Upcoming job</option><option value="other">Other</option></select></label><label>Due date<input name="date" type="date" required defaultValue={String(editingKeyDate?.date || '').slice(0, 10)} /></label><label>Detail<input name="detail" defaultValue={editingKeyDate?.detail || ''} placeholder="Optional detail" /></label><label className="checkbox-row"><input type="checkbox" name="recurring" defaultChecked={editingKeyDate?.recurring} /> Recurring</label><button className="primary-button" disabled={busy}>{busy ? 'Saving…' : editingKeyDate ? 'Save key date' : 'Add key date'}</button></form></SlideOver>

  <SlideOver open={panel === 'note'} title={editingNote ? 'Edit note' : 'Add note'} onClose={closePanel}><form className="drawer-form" key={editingNote?.id || 'note-new'} onSubmit={saveNote}><label>Note<textarea name="body" rows="6" required defaultValue={editingNote?.body || ''} placeholder="Site notes for this client…" /></label><label className="checkbox-row"><input type="checkbox" name="pinned" defaultChecked={editingNote?.pinned} /> Pin to top</label><label className="checkbox-row"><input type="checkbox" name="client_visible" defaultChecked={editingNote?.client_visible} /> Visible to client in portal</label><button className="primary-button" disabled={busy}>{busy ? 'Saving…' : editingNote ? 'Save note' : 'Save note'}</button></form></SlideOver>

  <CertificateForm open={panel === 'certificate'} onClose={closePanel} client={client} />

  <PropertyForm open={Boolean(editProperty)} property={editProperty} onClose={closePanel} onSaved={() => notify('Property saved')} />

  {/* Client edit form — ClientForm renders its own SlideOver; pass open/onClose
      directly. The old wrapper left the form's own open prop unset, so the
      drawer showed only the "Quick action / Edit client" header with no fields. */}
  <ClientForm
    open={Boolean(editClient)}
    onClose={closePanel}
    client={editClient}
    onSaved={() => notify('Client updated successfully')}
  />

  <ConfirmDialog
    open={Boolean(confirm)}
    title={
      confirm?.kind === 'confirm'
        ? 'Confirm payment?'
        : confirm?.kind === 'reverse'
        ? 'Reverse payment?'
        : confirm?.kind === 'delete-property'
        ? 'Remove property?'
        : confirm?.kind === 'delete-keydate'
        ? 'Delete key date?'
        : confirm?.kind === 'delete-doc'
        ? 'Delete document?'
        : confirm?.kind === 'void-invoice'
        ? 'Void invoice?'
        : confirm?.kind === 'delete' && confirm?.action === 'archive'
        ? 'Archive client?'
        : 'Reject payment claim?'
    }
    message={
      confirm?.kind === 'confirm'
        ? `Confirm ${money(confirm?.payment?.amount || 0)} paid by BACS and mark the invoice paid?`
        : confirm?.kind === 'reverse'
        ? `Reverse ${money(confirm?.payment?.amount || 0)}? The payment is archived (not deleted) and the invoice's paid status is recalculated.`
        : confirm?.kind === 'delete-property'
        ? `Remove "${confirm?.property?.name || confirm?.property?.address || 'this property'}"? Jobs keep their site snapshot; the property link is cleared.`
        : confirm?.kind === 'delete-keydate'
        ? `Delete "${confirm?.keyDate?.title || 'this key date'}" (${date(confirm?.keyDate?.date)})? This cannot be undone.`
        : confirm?.kind === 'delete-doc'
        ? `Delete "${confirm?.document?.filename}"? Only unacknowledged v1 documents can be deleted — use Archive for anything else.`
        : confirm?.kind === 'void-invoice'
        ? `Void ${confirm?.invoice?.number || 'this invoice'}? It becomes a terminal state — payments stay recorded but the invoice can't be re-activated.`
        : confirm?.kind === 'delete' && confirm?.action === 'archive'
        ? 'Archive this client? Their data will be preserved but hidden from the main client list.'
        : 'Reject this payment claim? The client will not be notified automatically.'
    }
    confirmLabel={
      confirm?.kind === 'confirm'
        ? 'Confirm payment'
        : confirm?.kind === 'reverse'
        ? 'Reverse payment'
        : confirm?.kind === 'delete-property'
        ? 'Remove property'
        : confirm?.kind === 'delete-keydate'
        ? 'Delete key date'
        : confirm?.kind === 'delete-doc'
        ? 'Delete document'
        : confirm?.kind === 'void-invoice'
        ? 'Void invoice'
        : confirm?.kind === 'delete' && confirm?.action === 'archive'
        ? 'Archive'
        : 'Reject claim'
    }
    onClose={() => setConfirm(null)}
    onConfirm={() => {
      if (confirm?.kind === 'confirm' || confirm?.kind === 'reject') {
        settlePayment(confirm.payment, confirm.kind === 'confirm' ? 'confirmed' : 'rejected');
      } else if (confirm?.kind === 'reverse') {
        reversePayment(confirm.payment);
      } else if (confirm?.kind === 'delete-property') {
        deleteProperty(confirm.property.id);
      } else if (confirm?.kind === 'delete-keydate') {
        deleteKeyDate(confirm.keyDate);
      } else if (confirm?.kind === 'delete-doc') {
        deleteDocument(confirm.document);
      } else if (confirm?.kind === 'void-invoice') {
        voidInvoice(confirm.invoice);
      } else if (confirm?.kind === 'delete' && confirm?.action === 'archive') {
        archiveClient();
      }
    }}
  />
</div>;
}
